/**
 * Operation Message Handler
 * Handles 'operation' message type for real-time operation broadcasts
 */

import { log } from '@/shared/utils/logger';
import { shouldSkipOperation, trackOperation, initializeOperationTracking } from './operationDeduplication';

interface OperationMessageHandlerParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WebSocket messages have dynamic shape
  message: any;
  sessionId: string;
  processedOperations: Set<string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- operations and messages have dynamic shape
  enqueueOperation: (operation: any, source: string, rawMessage?: any) => void;
}

/**
 * Handle 'operation' message type (real-time operation broadcasts)
 */
export function handleOperationMessage({
  message,
  sessionId,
  processedOperations,
  enqueueOperation
}: OperationMessageHandlerParams): void {
  if (!message.operation) return;

  const operation = message.operation;
  const operationId = operation.id || `${operation.type}-${operation.nodeId}-${Date.now()}`;
  const operationSessionId = operation.sessionId;

  log.debug('WebSocket', '🔵 OPERATION RECEIVED:', {
    type: operation.type,
    nodeId: operation.nodeId,
    operationId,
    sessionId: operationSessionId,
    projectId: message.projectId,
    source: message.source,
    timestamp: new Date().toISOString()
  });

  // Initialize tracking
  initializeOperationTracking();

  // CRITICAL: Skip MCP "operation" messages - they're duplicated in "operation-result"
  // Backend sends BOTH message types for MCP, we only need operation-result
  if (message.source === 'mcp') {
    log.debug('WebSocket', '🟡 SKIPPING MCP OPERATION (will process via operation-result)', {
      operationId,
      type: operation.type
    });
    return;
  }

  const isRemoteSessionOperation =
    Boolean(operationSessionId) && operationSessionId !== sessionId;

  // Browser-originated realtime writes also receive an authoritative operation-result
  // broadcast. Applying the optimistic remote "operation" first and then the canonical
  // "operation-result" again causes visible flicker in watcher browsers.
  if (isRemoteSessionOperation) {
    log.debug('WebSocket', '🟡 SKIPPING REMOTE OPERATION (will process via operation-result)', {
      operationId,
      type: operation.type,
      fromSession: operationSessionId,
      ourSession: sessionId,
    });
    return;
  }

  // Check if we should skip this operation (our own echo)
  if (shouldSkipOperation(operationSessionId, sessionId, operationId, operation.type, message.source)) {
    return;
  }

  log.debug('WebSocket', '🟢 PROCESSING OPERATION from other user:', {
    operationId,
    type: operation.type,
    fromSession: operationSessionId || 'server'
  });

  // Track the operation
  trackOperation(operationId, operationSessionId || sessionId, processedOperations);

  log.debug('WebSocket', '✅ OPERATION ACCEPTED AND WILL BE APPLIED:', {
    operationId,
    type: operation.type,
    fromSession: operationSessionId,
    ourSession: sessionId
  });

  // CRITICAL FIX: Use operation queue for sequential processing
  // This prevents race conditions when multiple operations arrive rapidly
  enqueueOperation(operation, 'operation', message);
}
